/* eslint-disable @typescript-eslint/no-explicit-any */
import { SupportedTools } from '@cyborg-monorepo/hunter/matchboi';
import { Enums } from './enums';

export interface PivotGeneratedContent {
  goal: string;
  query: string;
  title: string;
  matched: boolean;
  category: string;
  missingFields: string[];
  deployment_requirements: DeploymentRequirements;
  notes?: string;
  status?: `${Enums.QueryStatusEnum}`; 
  pivots?: any[] //tbd
}


export interface HuntCoverage {
  name: string;
  category?: string;
  huntCount: number;
  hunts: {
    name: string;
    id: string;
  }[];
  huntPackageCount: number;
  huntPackages: {
    title: string;
    uuid: string;
  }[];
}

export interface MitreHeatMapCoverage {
  name: string;
  huntTotalCount?: number;
  huntPackageTotalCount?: number;
  huntCount: number;
  huntPackageCount: number;
  hunts: {
    name: string;
    id: string;
  }[];
  huntPackages: {
    title: string;
    uuid: string;
  }[];
  subtechniques?: MitreHeatMapCoverage[];
}

export interface TitanRelatedReports {
  reportCount: number;
  reports?: Array<{
    uid: string;
    released: number;
    subject: string;
    portalReportUrl: string;
  }>;
}

export interface HuntPackagesAdded {
  name: string;
  id: string;
  huntPackagesAdded: number;
}
export interface QueryLogic {
  table: QueryLogicTable[];
  condition: string;
}
export interface QueryLogicTable {
  selection: string;
  field: string;
  value: string[];
  grouped: boolean;
}
export interface HuntPackageRecord {
  title: string;
  UUID: string;
  status: string;
  community: boolean;
  emerging_threat: boolean;
  author: string;
  created_date: string;
  last_updated: string;
  completed_date: string;
  source: string;
  sigma: string;
  source_origin: string;
  severity: string;
  query_logic: QueryLogic;
  dependencies: string[];
  description: string;
  references: {
    general: string[];
    analysis: string[];
    deep_dives: string[];
    blog_links: string[];
    malware_samples: string[];
  };
  complexity: {
    research: number;
    development: number;
    validation: number;
    enrichment: number;
    score: number;
  };
  response_actions: {
    analyst_runbook: string;
    playbook: string;
    recommended_response: string;
    mitigation_recommendations: string;
  };
  hunter_link: string;
  analyst_notes: string;
  content_uuids: string[];
  validation: string;
  validation_types: string[];
  validation_encoded_zip?: string;
  change_log: Changelog[];
  content: {
    uuids: string[];
    tools: string[];
    categories: string[];
    dependencies: string[];
    records: ContentRecord[];
  };
  context: {
    tooling: string[];
    threat_types: string[];
    threat_names: string[];
    threat_description: string;
    threat_categories: string[];
    actors: string[];
    target_industries: string[];
    target_countries: string[];
    target_regions: string[];
    source_countries: string[];
    source_regions: string[];
    operations: string[];
    target_oses: string[];
    target_os_versions: string[];
    campaigns: string[];
    attack_surfaces: string[];
    motivations: string[];
  };
  mapping: {
    mitre: {
      mitre_attack_tactics: string[];
      mitre_attack_techniques: string[];
      mitre_attack_technique_ids: string[];
      related_context: {
        actors: string[];
        target_industries: string[];
        source_regions: string[];
        target_countries: string[];
        threat_names: string;
        target_regions: string[];
        source_countries: string[];
      };
    };
    kill_chains: string[];
    diamond_model: string;
    exploit_or_vulns: string[];
    cwe_ids: string[];
  };
  related_intel: {
    Domains: string[];
    Emails: string[];
    Hashes: string[];
    IPs: string[];
    URLs: string[];
  };
  tags: Record<Enums.CyborgTagTypeEnum, string[]>;
}

// TODO: Define remainder of entire interface
export interface ContentRecord {
  UUID: string;
  author: string;
  title: string;
  arbiter: string;
  tier: string;
  date_created: string;
  last_updated: string;
  status: string;
  content_ingestion: string;
  platform_type: string;
  tool: SupportedTools;
  goal: string;
  data_source: string;
  decay_timer: number;
  decayed: boolean;
  source: string;
  threat_description: string;
  deployment_requirements: DeploymentRequirements;
  content: string;
  additional_queries: string[];
  change_log: Changelog[];
}

export interface Changelog {
  date: string;
  note: string;
}

export interface DeploymentRequirements {
  categories: string[];
  searchable_data: string[];
  deployment_steps: string;
}

export interface Profile {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  role: Enums.UserRoleEnum;
  isFirstLogin: boolean;
  createdAt: Date;
  company: Company;
  phoneNumber: string;
}

export interface MinimalProfile {
  firstName: string;
  lastName: string;
  email: string;
  role: Enums.UserRoleEnum;
}

export interface ExpandedProfile {
  fullName: string;
}

//Threat Profile
export interface ThreatProfileContext {
  actors: string[];
  motivations: string[];
  target_regions: string[];
  target_countries: string[];
  target_industries: string[];
  source_regions: string[];
  source_countries: string[];
  target_oses: string[];
  attack_surfaces: string[];
  threat_categories: string[];
  threat_types: string[];
  threat_name: string;
}
export interface ThreatProfileIndicatorTypeTagMappings {
  stage_0_hash: ThreatProfileCommandAndControlClass;
  stage_0_link: ThreatProfileCommandAndControlClass;
  stage_1: ThreatProfileCommandAndControlClass;
  stage_2: PhishingLink;
  stage_3: PhishingLink;
  command_and_control: ThreatProfileCommandAndControlClass;
  exploitkit_link: ThreatProfileCommandAndControlClass;
  phishing_link: PhishingLink;
}

export interface ThreatProfileCommandAndControlClass {
  title: string;
  feed_type: string;
  threat_feed_category: string;
  ioc_type: ThreatProfileCommandAndControlElement[];
  threat_category: string;
  confidence: string;
  threat_type: string;
  threat_name: string;
  mitre_attack_tactic: string[];
  mitre_attack_technique: string[];
  kill_chain: string;
  impact: string;
  analyst_notes: string;
}
export enum ThreatProfileCommandAndControlElement {
  Domain = 'domain',
  IP = 'ip',
  Port = 'port',
  URL = 'url',
}

export interface PhishingLink {
  title: string;
  feed_type: string;
  threat_feed_category: string;
  ioc_type: string;
  threat_category: string;
  confidence: string;
  threat_type: string;
  threat_name: string;
  mitre_attack_tactic: string[];
  mitre_attack_technique: string[];
  kill_chain: string;
  impact: string;
  analyst_notes: string;
}

export interface ThreatProfileIndicatorTypes {
  phishing_link: string[];
  stage_0_hash: string[];
  stage_0_link: string[];
  stage_2: string[];
  command_and_control: ThreatProfileCommandAndControlElement[];
  exploitkit_link: string[];
  stage_3: string[];
  stage_1: string[];
}
export interface ThreatProfile {
  UUID: string;
  profile_id: string;
  profile_name: string; // deprecated, duplicated to title to migrate for field consistency
  title: string;
  profile_type: string;
  also_known_as: string[];
  overview: string;
  targeting: string;
  delivery: string;
  installation: string;
  persistence: string;
  modules: string;
  communication: string;
  references: string[];
  last_updated: Date;
  created_date: Date;
  impact: string[];
  context: ThreatProfileContext;
  mapping: ThreatProfileMapping;
  indicator_types: ThreatProfileIndicatorTypes;
  indicator_type_tag_mappings: ThreatProfileIndicatorTypeTagMappings;
  tags: Record<Enums.CyborgTagTypeEnum, string[]>;
}

export interface ThreatProfileMapping {
  mitre: ThreatProfileMappingMitre;
}
export interface ThreatProfileMappingMitre {
  mitre_techniques_overall: string[];
  mitre_software_id: string;
}

// CompanyRO
export type CompanyToolMetadata = {
  [key in SupportedTools]?: {
    url: string;
  };
};

export interface Company {
  id: number;
  accountExternalID: string;
  name: string;
  industry: string;
  city: string;
  state: string;
  huntModuleAccess: boolean;
  country: string;
  status: Enums.CompanyStatus;
  expires: Date;
  memberSince: Date;
  toolMetadata?: CompanyToolMetadata;
  admin?: {
    id: number;
    firstName: string;
    lastName: string;
  };
}

export interface PaginateDeploy {
  items: Array<DeployItem>;
  meta: Meta;
}

export interface Deploy {
  id: number;
  huntPackageUUID: string;
  createdAt: string;
  deployedAt: string;
}

export interface DeployItem {
  id: number;
  huntPackage: HuntPackageRecord;
  deployedAt: string;
  createdAt: string;
  huntPackageUUID: string;
}

export interface Meta {
  currentPage: number;
  itemCount: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

export interface FavoritesList {
  items: Array<Favorite>;
  meta: Meta;
  links: {
    first: string;
    previous: string;
    next: string;
    last: string;
  };
}

export interface Favorite {
  index: Enums.CyborgIndexEnum;
  targetUuid: string;
}

export interface HuntList {
  items: Array<Hunt>;
  meta: Meta;
}

export interface HuntTemplateList {
  items: Array<HuntTemplateWithCount>;
  meta: Meta;
}

export interface HuntTemplateWithCount extends HuntTemplate {
  huntCount: string;
}

export interface Context {
  actors: string[];
  operations: string[];
  threat_names: string[];
}

export interface Mitre {
  mitre_attack_technique_ids: string[];
  mitre_attack_techniques: string[];
  mitre_attack_tactics: string[];
}

export interface Mapping {
  mitre: Mitre;
  exploit_or_vulns: string[];
}

export interface CollectionRecord {
  UUID: string;
  title: string;
  status: string;
  created_date: string;
  last_updated: string;
  threat_summary: string;
  synopsis: string;
  references: string[];
  context: Context;
  mapping: Mapping;
  hunter_packages: string[];
  threat_profiles: string[];
  tags: Record<Enums.CyborgTagTypeEnum, string[]>;
}

export interface Hunt {
  id: string;
  created: Date;
  lastModified: Date;
  name: string;
  priority: Enums.PriorityEnum;
  status: Enums.StatusEnum;
  outcome: Enums.OutcomeEnum;
  company: Company;
  author: Profile;
  assignee: Profile;
  huntTemplate: HuntTemplate;
  notes: string;
  huntPackages: ExpandedHuntPackageRecord[];
  environments: Environment[];
  executiveSummary?: string;
  hypothesis?: string;
  abstract?: string;
  conclusion?: string;
  editing?: boolean;
  outcomeDependent?: string;
  archived: boolean;
}

export interface GeneratedContent {
  goal: string;
  query: string;
  title: string;
  matched: boolean;
  category: string;
  missingFields: string[];
  deployment_requirements: DeploymentRequirements;
  notes?: string;
  status?: Enums.QueryStatusEnum;
  pivots?: PivotGeneratedContent[]
}

export interface GeneratedContentDetails {
  tool: SupportedTools;
  content: Array<{
    query: string;
    category: string;
    deployment_requirement?: DeploymentRequirements;
  }>;
}

export interface ExpandedGeneratedContentDetails
  extends GeneratedContentDetails {
  name?: string;
  deployment_requirements: DeploymentRequirements;
  content: Content[];
}

export interface EnvironmentAnalysis {
  id: string;
  name: string;
  content: any;
}

export interface Content {
  query: string;
  category: string;
  notes?: string;
}

export interface ExpandedHuntPackageRecord extends HuntPackageRecord {
  analysis?: Environment[];
  originalAnalysis?: Environment[];
  reportAnalysis?: Environment[];
}
export interface HuntTemplate {
  id: string;
  created: Date;
  lastModified: Date;
  name: string;
  hypothesis: string;
  description: string;
  priority: Enums.PriorityEnum;
  company: Company;
  author: Profile;
  defaultAssignee: Profile;
  hunts: Hunt[];
  huntPackages: ExpandedHuntPackageRecord[];
  environments: Environment[];
  archived: boolean;
  savedQueries: SavedQuery[];
}

export interface Environment {
  id: string;
  created: Date;
  lastModified: Date;
  name: string;
  description: string;
  company: Company;
  tools: Tool[];
}

export interface Tool {
  generatedContent?: {
    Hunt?: GeneratedContent[];
    Behavior?: GeneratedContent[];
  };
  id: string;
  created: Date;
  lastModified: Date;
  name: string;
  product: SupportedTools;
  rootUrl: string;
  environment: Environment;
  fieldMappings?: FieldMappings[];
}

export interface FieldMappings {
  name: string;
  index?: string;
  sourceType?: string;
  category: string;
  fields?: {
    [ccmField: string]: string;
  }[];
  toolSpecific?: {
    [ccmField: string]: string;
  }[];
}

export interface SavedQuery {
  savedContent: GeneratedContent;
  tool: Tool;
  huntPackageName: string;
  huntPackUUID: string;
  environment: Environment;
}

export interface HuntSummary {
  id: string;
  name: string;
  priority?: Enums.PriorityEnum;
  status?: Enums.StatusEnum;
  outcome?: Enums.OutcomeEnum;
  numHuntPackages?: number;
}
export interface HuntTemplateSummary {
  id: string;
  name: string;
  numHuntPackages: number;
}

export interface ApiKey {
  id: string;
  createdAt: string;
  name: string;
}

export interface CreatedApiKey extends ApiKey {
  key: string;
}

export interface UpdatePassword {
  password: string;
  oldPassword?: string;
}
